import { g, Tool, layers } from '@hcie/core';
import { renderLayers, getActiveLayer, updateLayerPanel } from '../layers';
import { tempCtx, tempCanvas, can } from '../rendering-loop';
import { simplifyPath, buildLassoSelection } from '@hcie/tools';
import { SelectionMode } from '@hcie/shared';
import { applySelectionMaskToCtx } from './selection-utils';

declare var historyManager: any;
declare function makeNamedDrawAction(layerIndex: number, prev: ImageData, next: ImageData, desc: string): any;

export class FinishHandler {
    static finishDrawing(e: MouseEvent | null): boolean {
        if (!g.drawing && !(g as any)._isResizingCrop && !(g as any)._isMovingCrop && !g.movingSelection) return false;

        const activeLayer = getActiveLayer();
        if (!activeLayer) return false;

        const shift = e ? e.shiftKey : false;
        const alt = e ? e.altKey : false;

        try {
            // Move selection/content tools should not record raster draw undo here.
            // Selection undo is captured by apps/editor selection-coordinator.
            if (g.current_tool.id === Tool.MoveSelection.id || g.current_tool.id === Tool.MoveContent.id) {
                g.movingSelection = false;
                g.drawing = false;
                renderLayers();
                return true;
            }

            // Specific tool finish logic
            if (g.current_tool.id === Tool.Lasso.id) {
                this.finishLasso(shift, alt);
                return true;
            }
            if (g.current_tool.id === Tool.Crop.id) {
                this.finishCrop();
                return true;
            }
            if (g.current_tool.id === Tool.PolySelect.id) {
                if (g.movingSelection) {
                    g.movingSelection = false;
                    g.drawing = false;
                    renderLayers();
                }
                return true;
            }

            // Universal commit for raster tools
            if (tempCtx) applySelectionMaskToCtx(tempCtx);

            const isVectorShapeTool = [Tool.Line.id, Tool.Circle.id, Tool.Rectangle.id, Tool.Rounded_Rectangle.id, Tool.Ellipse.id].includes(g.current_tool.id);
            const isVectorLayer = activeLayer.type === 'vector';

            // Vector layer: enter ghost edit mode (DOM overlay) instead of rasterizing into ctx.
            if (isVectorShapeTool && isVectorLayer) {
                console.log(`[VectorGhost] mouseUp tool=${g.current_tool.id} layer=${g.activeLayerIndex} start=(${Math.round(g.startX)},${Math.round(g.startY)}) end=(${Math.round(g.pX)},${Math.round(g.pY)})`);
                const vtm = (window as any).vectorToolManager;
                if (vtm && typeof vtm.startNewFromTool === 'function') {
                    vtm.startNewFromTool(g.current_tool.id, g.startX, g.startY, g.pX, g.pY, g.activeLayerIndex);
                } else {
                    console.warn('[VectorGhost] window.vectorToolManager missing or invalid', vtm);
                }
                if (tempCtx) tempCtx.clearRect(0, 0, tempCtx.canvas.width, tempCtx.canvas.height);
                return true;
            }

            // Feature BUG: Ghost Mode for selection tools is different from delay-rasterization for vector tools.
            // If it's a vector-type tool on a raster layer, we should only prevent auto-commit IF user explicitly chose editing mode.
            const delayRasterization = isVectorShapeTool && !isVectorLayer && (g as any).settings?.vector?.ghostMode;

            if (delayRasterization) {
                // Ghost Mode (Raster): Start ghost edit via vectorToolManager and delay applying to raster until OK.
                const vtm = (window as any).vectorToolManager;
                if (vtm && typeof vtm.startNewFromTool === 'function' && activeLayer.ctx) {
                    try {
                        (window as any).vectorRasterGhost = {
                            layerIndex: g.activeLayerIndex,
                            prev: activeLayer.ctx.getImageData(0, 0, g.image_width, g.image_height),
                            desc: 'Vector Shape',
                        };
                    } catch {
                        (window as any).vectorRasterGhost = { layerIndex: g.activeLayerIndex, prev: null, desc: 'Vector Shape' };
                    }
                    vtm.startNewFromTool(g.current_tool.id, g.startX, g.startY, g.pX, g.pY, g.activeLayerIndex);
                }
                if (tempCtx) tempCtx.clearRect(0, 0, tempCtx.canvas.width, tempCtx.canvas.height);
                return true;
            }

            if (g.current_tool.id === Tool.Eraser.id) {
                if (tempCanvas && activeLayer.ctx) {
                    activeLayer.ctx.save();
                    activeLayer.ctx.globalCompositeOperation = 'destination-out';
                    activeLayer.ctx.drawImage(tempCanvas as HTMLCanvasElement, 0, 0);
                    activeLayer.ctx.restore();
                }
            } else if (tempCanvas && activeLayer.ctx) {
                activeLayer.ctx.drawImage(tempCanvas as HTMLCanvasElement, 0, 0);
            }

            this.recordUndo(activeLayer);

        } catch (e) {
            console.error("[FinishHandler] Error:", e);
        } finally {
            this.resetState();
        }
        return true;
    }

    private static finishLasso(shift: boolean, alt: boolean) {
        if (g.movingSelection) {
            g.movingSelection = false;
        } else if ((window as any).lassoPoints) {
            let mode: SelectionMode = "replace" as SelectionMode;
            if (shift) mode = "add" as SelectionMode;
            if (alt) mode = "subtract" as SelectionMode;
            
            const tolerance = (g as any).settings?.selection?.lassoSmoothing || 0;
            let p = (window as any).lassoPoints;
            if (tolerance > 0.1) p = simplifyPath(p, tolerance);
            
            buildLassoSelection(p, mode);
            if ((g as any).settings?.selection?.ghostMode) {
                (g as any)._lassoEditPoints = [...p];
                (g as any)._lastSelectionMode = mode;
            }
            (window as any).lassoPoints = (g as any).settings?.selection?.ghostMode ? [...p] : [];
        }
        g.isSelectionActive = true;
        g.selectionPreviewBorder = [];
        if (!(g as any).settings?.selection?.ghostMode) g.drawing = false;
        if (tempCtx) tempCtx.clearRect(0, 0, tempCtx.canvas.width, tempCtx.canvas.height);
        (g as any).needsRender = true;
    }

    private static finishCrop() {
        const w = (g as any);
        if (!w._isResizingCrop && !w._isMovingCrop) {
            const x = Math.min(g.startX, g.pX), y = Math.min(g.startY, g.pY);
            const wd = Math.abs(g.pX - g.startX), ht = Math.abs(g.pY - g.startY);
            if (wd > 2 && ht > 2) w.lastCropRect = { x, y, w: wd, h: ht };
        }
        w._isResizingCrop = false; w._isMovingCrop = false;
        g.drawing = false;
        renderLayers();
    }

    private static resetState() {
        g.drawing = false;
        g.movingSelection = false;
        (g as any)._isResizingCrop = false;
        (g as any)._isMovingCrop = false;
        // [DEBUG 2026-04-07][TaskID: GDS-Stabilize] Explicit cursor reset per tool category
        const previewTools = [Tool.Pen.id, Tool.Brush.id, Tool.Eraser.id, Tool.Spray.id, 'btn-red-eye'];
        const selectionTools = [
            'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
            'btn-lasso', 'btn-poly-select', 'btn-single-row-select', 'btn-single-column-select'
        ];
        if (can) {
            if (previewTools.includes(g.current_tool.id)) can.style.cursor = 'none';
            else if (selectionTools.includes(g.current_tool.id)) can.style.cursor = 'crosshair';
            else can.style.cursor = 'default';
        }
        if (tempCtx) tempCtx.clearRect(0, 0, tempCtx.canvas.width, tempCtx.canvas.height);
        (g as any).needsRender = true;
        if (typeof updateLayerPanel === 'function') updateLayerPanel();
    }

    private static recordUndo(layer: any) {
        if (!layer || !layer.ctx || !g.previousLayerState) return;
        const newData = layer.ctx.getImageData(0, 0, g.image_width, g.image_height);
        const desc = this.getToolDescription();
        if (typeof historyManager !== 'undefined') {
            historyManager.push((window as any).makeNamedDrawAction(g.activeLayerIndex, g.previousLayerState, newData, desc));
        }
        g.previousLayerState = null;
    }

    private static getToolDescription(): string {
        const id = g.current_tool.id;
        if (id === 'btn-red-eye') return 'Red Eye Removal';
        if (id === Tool.Eraser.id) return 'Eraser';
        if (id === Tool.Pen.id) return 'Pen';
        if (id === Tool.Brush.id) return 'Brush';
        if (id === Tool.Spray.id) return 'Spray';
        if (id === Tool.Flood_Fill.id) return 'Fill';
        return 'Draw';
    }
}
